import React, { useCallback, useContext, useRef } from "react";
import { useModal } from "react-modal-hook";
import { buildGuestWorkflowURL } from "common/utils/url";
import {
  AccessControlEntityType,
  AccessControlPolicy,
  GetWorkflowQuery,
  useUpdateWorkflowCollaboratorsMutation,
  useWorkflowCollaboratorsQuery,
  WorkflowCollaboratorFieldsFragment,
} from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { AuthContext } from "../Authorization/AuthContext";
import { CommonModal } from "../Common/CommonModal";
import { Loading } from "../Common/Icons/Loading";
import Modal from "../Common/Modal";
import { Collaborator, PermissionOption, ShareForm } from "../Common/ShareForm";

const extractCollaboratorData = (
  collab: WorkflowCollaboratorFieldsFragment,
) => {
  const user = collab.publicUser;
  const name = user ? `${user.firstName} ${user.lastName}`.trim() : "Unknown";
  const description = user?.email;
  const role = collab.publicUser?.userGroups?.[0]?.name;
  const userGroupId = collab.publicUser?.userGroups?.[0]?.id;

  return {
    name: name || description || "Unknown",
    description,
    role,
    userGroupId,
  };
};

const mapCollaboratorsFromData = (
  collaborators: WorkflowCollaboratorFieldsFragment[],
): Collaborator[] => {
  if (!collaborators) return [];

  const permissionRank: Record<string, number> = {
    createResource: 3,
    updateResource: 2,
    viewResource: 1,
  };

  const collaboratorMap = new Map<string, Collaborator>();

  collaborators.forEach(collab => {
    const { name, description, role, userGroupId } =
      extractCollaboratorData(collab);

    const existing = collaboratorMap.get(collab.entityId);
    const currentRank = permissionRank[collab.policy] || 0;
    const existingRank =
      existing ? permissionRank[existing.permission] || 0 : -1;

    if (currentRank > existingRank) {
      collaboratorMap.set(collab.entityId, {
        id: collab.entityId,
        name,
        description,
        permission: collab.policy,
        role,
        userGroupId,
      });
    }
  });

  return Array.from(collaboratorMap.values()).sort((a, b) =>
    a.name.localeCompare(b.name),
  );
};

export interface ShareWorkflowModalProps {
  workflow: Pick<NonNullable<GetWorkflowQuery["workflow"]>, "id">;
  onClose: () => void;
  clipboard?: Pick<Clipboard, "writeText">;
  onInvite?: (collaborators: Collaborator[]) => void;
  onRemove?: (collaboratorId: string) => void;
}

const WORKFLOW_PERMISSION_OPTIONS: PermissionOption[] = [
  { value: "createResource", label: "Can create" },
  { value: "updateResource", label: "Can update" },
  { value: "viewResource", label: "Can view" },
];

export const ShareWorkflowModalContent = ({
  workflow,
  onClose,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onInvite" | "onRemove">) => {
  const { account } = useContext(AuthContext);
  const { addSuccessToast, addErrorToast } = useStatusToasts();

  const { data: workflowData, loading: isLoading } =
    useWorkflowCollaboratorsQuery({
      variables: { workflowId: workflow.id },
      fetchPolicy: "no-cache",
      notifyOnNetworkStatusChange: true,
    });

  const [updateWorkflowCollaborators, { loading: isSubmitting }] =
    useUpdateWorkflowCollaboratorsMutation();

  const initialCollaborators = mapCollaboratorsFromData(
    workflowData?.workflow?.collaborators ?? [],
  );

  const currentCollaboratorsRef = useRef<Collaborator[]>([]);

  const handleStateChange = useCallback((collaborators: Collaborator[]) => {
    currentCollaboratorsRef.current = collaborators;
  }, []);

  const handleCopyLink = useCallback(async () => {
    const url = buildGuestWorkflowURL({
      subdomain: account!.subdomain,
      workflowId: workflow.id,
    });
    await clipboard.writeText(url);
    addSuccessToast("Link copied to clipboard");
  }, [account, workflow.id, clipboard, addSuccessToast]);

  const handleSubmit = useCallback(async () => {
    try {
      await updateWorkflowCollaborators({
        variables: {
          data: {
            workflowId: workflow.id,
            collaborators: currentCollaboratorsRef.current.map(c => ({
              entityId: c.id,
              entityType: AccessControlEntityType.PUBLIC_USER,
              policy: AccessControlPolicy.VIEW,
            })),
          },
        },
      });

      track("Shared Workflow", {
        workflowId: workflow.id,
      });

      addSuccessToast(`This workflow has been updated successfully`);
      onClose();
    } catch {
      addErrorToast(`Failed to update workflow`);
    }
  }, [
    workflow.id,
    updateWorkflowCollaborators,
    addSuccessToast,
    addErrorToast,
    onClose,
  ]);

  return (
    <CommonModal
      title="Share workflow"
      size="large"
      onCancel={onClose}
      primaryButtonText={isSubmitting ? "Saving..." : "Share"}
      onSubmit={handleSubmit}
      disabled={isSubmitting || isLoading}
      secondaryButtons={[
        {
          text: "Copy link",
          onClick: handleCopyLink,
        },
      ]}
      content={
        isLoading ?
          <Loading size="medium" />
        : <ShareForm
            searchPlaceholder="Add email address"
            sectionLabel="Who has access"
            infoText={{
              text: `All Forerunner team members can view this workflow.`,
              linkText: "Add a team member",
              linkHref: "/settings/users/team",
            }}
            permissionOptions={WORKFLOW_PERMISSION_OPTIONS}
            initialCollaborators={initialCollaborators}
            onStateChange={handleStateChange}
            buttonText="Add"
            showRemoveOption={true}
            includeUsers={false}
            includePublicUsers={true}
            showRole
          />
      }
    />
  );
};

export const useShareWorkflowModal = ({
  workflow,
  clipboard = navigator.clipboard,
}: Omit<ShareWorkflowModalProps, "onClose" | "onInvite" | "onRemove">) => {
  const [showModal, hideModal] = useModal(
    () => (
      <Modal onRequestClose={hideModal}>
        <ShareWorkflowModalContent
          workflow={workflow}
          onClose={hideModal}
          clipboard={clipboard}
        />
      </Modal>
    ),
    [workflow, clipboard],
  );

  return [showModal, hideModal] as const;
};
